/**
 * PageBanner — Shared animated page title/subtitle header component.
 * Matches StandardPageLayout animation: opacity:0,x:-20 → opacity:1,x:0
 * key={title} on motion elements ensures animation re-fires on section switch.
 */
import React from 'react';
import { motion } from 'framer-motion';


const FADE_IN = {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
};

export const PageBanner = ({
    icon,
    title,
    subtitle,
    accentGradient = 'from-indigo-500 to-violet-600',
    actions,
    className = '',
}) => (
    <div className={`shrink-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 ${className}`}>
        <div className="flex items-center justify-between px-6 py-4 gap-4">
            {/* Left: icon + title + subtitle */}
            <div className="flex items-start gap-3 min-w-0">
                {icon && (
                    <motion.div
                        key={title}
                        {...FADE_IN}
                        transition={{ duration: 0.35 }}
                        className={`shrink-0 p-2.5 rounded-xl bg-gradient-to-br ${accentGradient} text-white shadow-lg mt-0.5`}
                    >
                        {icon}
                    </motion.div>
                )}
                <div className="min-w-0">
                    <motion.h1
                        key={title}
                        {...FADE_IN}
                        transition={{ duration: 0.35 }}
                        className="text-[2.125rem] font-bold text-gray-900 dark:text-gray-100 leading-tight tracking-tight truncate"
                    >
                        {title}
                    </motion.h1>
                    {subtitle && (
                        <motion.p
                            key={subtitle}
                            {...FADE_IN}
                            transition={{ duration: 0.35, delay: 0.1 }}
                            className="text-base text-gray-500 dark:text-gray-400 mt-1 leading-snug"
                        >
                            {subtitle}
                        </motion.p>
                    )}
                </div>
            </div>

            {/* Right: action buttons */}
            {actions && (
                <motion.div
                    key={title + '_actions'}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, delay: 0.15 }}
                    className="shrink-0 flex items-center gap-2"
                >
                    {actions}
                </motion.div>
            )}
        </div>
    </div>
);

export default PageBanner;
